import React, { useState } from 'react';
import { UserIcon, StoreIcon, BookIcon, HeadphonesIcon, CreditCardIcon, TruckIcon, SaveIcon, PlusIcon, TrashIcon, ImageIcon, MailIcon, TagIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
type VendorType = 'store' | 'author' | 'narrator';
interface SocialLink {
  platform: string;
  url: string;
  logo: string;
}
interface VendorProfileData {
  // Basic Info
  name: string;
  email: string;
  profileImage: File | null;
  bannerImage: File | null;
  // Store Info
  storeName: string;
  storeDescription: string;
  storePolicy: string;
  shippingPolicy: string;
  returnPolicy: string;
  paymentMethods: string[];
  // Author Info
  authorPenName: string;
  authorBio: string;
  publishedBooks: string[];
  writingGenres: string[];
  currentProjects: string[];
  publishingHistory: string;
  // Narrator Info
  narratorName: string;
  voiceTypes: string[];
  audioEquipment: string;
  recordingSpace: string;
  accentsProficient: string[];
  sampleClips: File[];
  socialLinks: SocialLink[];
  contentPreferences: {
    include: string[];
    exclude: string[];
  };
}
const VendorProfileSettings = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [activeTab, setActiveTab] = useState<VendorType>('store');
  const [saved, setSaved] = useState(false);
  const [newPlatform, setNewPlatform] = useState('');
  const [showPlatformOptions, setShowPlatformOptions] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeList, setActiveList] = useState<'include' | 'exclude'>('include');
  const platformOptions = [{
    name: 'Instagram',
    logo: 'https://logo.clearbit.com/instagram.com'
  }, {
    name: 'Twitter',
    logo: 'https://logo.clearbit.com/twitter.com'
  }, {
    name: 'Facebook',
    logo: 'https://logo.clearbit.com/facebook.com'
  }, {
    name: 'LinkedIn',
    logo: 'https://logo.clearbit.com/linkedin.com'
  }, {
    name: 'YouTube',
    logo: 'https://logo.clearbit.com/youtube.com'
  }, {
    name: 'TikTok',
    logo: 'https://logo.clearbit.com/tiktok.com'
  }, {
    name: 'Goodreads',
    logo: 'https://logo.clearbit.com/goodreads.com'
  }, {
    name: 'Amazon Author Page',
    logo: 'https://logo.clearbit.com/amazon.com'
  }];
  const [formData, setFormData] = useState<VendorProfileData>({
    name: '',
    email: '',
    profileImage: null,
    bannerImage: null,
    storeName: '',
    storeDescription: '',
    storePolicy: '',
    shippingPolicy: '',
    returnPolicy: '',
    paymentMethods: [],
    authorPenName: '',
    authorBio: '',
    publishedBooks: [],
    writingGenres: [],
    currentProjects: [],
    publishingHistory: '',
    narratorName: '',
    voiceTypes: [],
    audioEquipment: '',
    recordingSpace: '',
    accentsProficient: [],
    sampleClips: [],
    socialLinks: [{
      platform: 'Personal Website',
      url: '',
      logo: 'https://logo.clearbit.com/website.com'
    }],
    contentPreferences: {
      include: [],
      exclude: []
    }
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof VendorProfileData) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        [field]: file
      }));
    }
  };
  const handleArrayInput = (field: keyof VendorProfileData, value: string) => {
    if (Array.isArray(formData[field])) {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] as string[]), value]
      }));
    }
  };
  const removeArrayItem = (field: keyof VendorProfileData, index: number) => {
    if (Array.isArray(formData[field])) {
      setFormData(prev => ({
        ...prev,
        [field]: (prev[field] as string[]).filter((_, i) => i !== index)
      }));
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };
  const getContentLabel = () => {
    switch (activeTab) {
      case 'author':
        return {
          title: 'Content You Write',
          includeLabel: 'Topics I write',
          excludeLabel: "Topics I don't write"
        };
      case 'narrator':
        return {
          title: 'Content You Narrate',
          includeLabel: 'Content I narrate',
          excludeLabel: "Content I don't narrate"
        };
      case 'store':
        return {
          title: 'Store Content',
          includeLabel: 'Content I sell',
          excludeLabel: "Content I don't sell"
        };
    }
  };
  const inputClass = `w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.inputFocus}`;
  const labelClass = `block text-sm font-medium mb-1 ${dk.textSecondary}`;
  return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-12655" data-mp="1a6ci7wo2cjnj">
      {/* Profile Type Selection */}
      <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12656" data-mp="1f81u1ej411co">
        <div className="grid grid-cols-3 gap-4" data-id="element-12657" data-mp="1k4saw42qo0z5">
          <button type="button" onClick={() => setActiveTab('store')} className={`p-4 rounded-lg border-2 transition-colors ${activeTab === 'store' ? `${rt.borderActive} ${rt.bgLight}` : dk.border}`} data-id="element-12658" data-mp="1k40nvhn7jbkh">
            <StoreIcon className={`h-6 w-6 mx-auto mb-2 ${activeTab === 'store' ? rt.textPrimary : dk.textMuted}`} data-id="element-12659" data-mp="1xw6uod4h0679" />
            <div className={`text-center font-medium ${activeTab === 'store' ? rt.textPrimary : dk.textMuted}`} data-id="element-12660" data-mp="22u8ej09q09js">
              Store
            </div>
          </button>
          <button type="button" onClick={() => setActiveTab('author')} className={`p-4 rounded-lg border-2 transition-colors ${activeTab === 'author' ? `${rt.borderActive} ${rt.bgLight}` : dk.border}`} data-id="element-12661" data-mp="7r3marnrzd2s">
            <BookIcon className={`h-6 w-6 mx-auto mb-2 ${activeTab === 'author' ? rt.textPrimary : dk.textMuted}`} data-id="element-12662" data-mp="crzl4qt9s91c" />
            <div className={`text-center font-medium ${activeTab === 'author' ? rt.textPrimary : dk.textMuted}`} data-id="element-12663" data-mp="qlqykaa4gd2c">
              Author
            </div>
          </button>
          <button type="button" onClick={() => setActiveTab('narrator')} className={`p-4 rounded-lg border-2 transition-colors ${activeTab === 'narrator' ? `${rt.borderActive} ${rt.bgLight}` : dk.border}`} data-id="element-12664" data-mp="10frbuzfk4om5">
            <HeadphonesIcon className={`h-6 w-6 mx-auto mb-2 ${activeTab === 'narrator' ? rt.textPrimary : dk.textMuted}`} data-id="element-12665" data-mp="2xax99r9hdcy5" />
            <div className={`text-center font-medium ${activeTab === 'narrator' ? rt.textPrimary : dk.textMuted}`} data-id="element-12666" data-mp="1cu62he6itdsy">
              Narrator
            </div>
          </button>
        </div>
      </div>
      {/* Basic Information */}
      <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12667" data-mp="180behyjvuco2">
        <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12668" data-mp="u1va4e9hsy93">
          <UserIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12669" data-mp="z17umscw5t8i" />
          Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12670" data-mp="k47cngki5cn8">
          <div data-id="element-12671" data-mp="p2ikxmv5ff3c">
            <label htmlFor="name" className={labelClass} data-id="element-12672" data-mp="p28xa5xktk0t">
              Full Name
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className={inputClass} required data-id="element-12673" data-mp="2egx6jyavelum" />
          </div>
          <div data-id="element-12674" data-mp="17996bpv9cswz">
            <label htmlFor="email" className={labelClass} data-id="element-12675" data-mp="1283ctptrx1me">
              Email
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className={inputClass} required data-id="element-12676" data-mp="xhd88et692sr" />
          </div>
        </div>
        {/* Profile Images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6" data-id="element-12677" data-mp="jhu19pc3jxi1">
          <div data-id="element-12678" data-mp="jjoppdmku7qv">
            <label className={labelClass} data-id="element-12679" data-mp="eihhjdgiv6jo">Profile Picture</label>
            <div className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${dk.border} ${dk.surface}`} data-id="element-12680" data-mp="eir8b39e5hqf">
              <div className="space-y-1 text-center" data-id="element-12681" data-mp="28x1u12uovvrz">
                {formData.profileImage ? <img src={URL.createObjectURL(formData.profileImage)} alt="Profile preview" className="h-24 w-24 rounded-full object-cover mx-auto mb-4" data-id="element-12682" data-mp="23wxbz8e4uszn" /> : <UserIcon className={`h-12 w-12 mx-auto ${dk.textMuted}`} data-id="element-12683" data-mp="2gdye1xv3yhty" />}
                <div className={`flex text-sm ${dk.textSecondary}`} data-id="element-12684" data-mp="2l8ujq0t9p3pj">
                  <label className={`relative cursor-pointer rounded-md font-medium focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 ${rt.textPrimary} ${rt.inputFocus} ${dk.surface}`} data-id="element-12685" data-mp="2q4jc9k6vdkcc">
                    <span data-id="element-12686" data-mp="22g9viwoijms2">Upload a file</span>
                    <input type="file" className="sr-only" onChange={e => handleFileChange(e, 'profileImage')} accept="image/*" data-id="element-12687" data-mp="22fgveek27viw" />
                  </label>
                </div>
                <p className={`text-xs ${dk.textMuted}`} data-id="element-12688" data-mp="3itqhf20tx86k">PNG, JPG up to 10MB</p>
              </div>
            </div>
          </div>
          <div data-id="element-12689" data-mp="11yywcndhipz7">
            <label className={labelClass} data-id="element-12690" data-mp="11x4j7v8gg25o">Banner Image</label>
            <div className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${dk.border} ${dk.surface}`} data-id="element-12691" data-mp="15zhrorel6e65">
              <div className="space-y-1 text-center" data-id="element-12692" data-mp="1fqm2rg155evp">
                {formData.bannerImage ? <img src={URL.createObjectURL(formData.bannerImage)} alt="Banner preview" className="h-24 w-full object-cover rounded-md mb-4" data-id="element-12693" data-mp="j0cxlf5fxayg" /> : <ImageIcon className={`h-12 w-12 mx-auto ${dk.textMuted}`} data-id="element-12694" data-mp="s09rbv6ot00d" />}
                <div className={`flex text-sm ${dk.textSecondary}`} data-id="element-12695" data-mp="x0xf87zmd0qd">
                  <label className={`relative cursor-pointer rounded-md font-medium focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 ${rt.textPrimary} ${rt.inputFocus} ${dk.surface}`} data-id="element-12696" data-mp="23gpy7046e8bm">
                    <span data-id="element-12697" data-mp="3swk30dop7dfa">Upload a file</span>
                    <input type="file" className="sr-only" onChange={e => handleFileChange(e, 'bannerImage')} accept="image/*" data-id="element-12698" data-mp="3nxpc0ogkuik3" />
                  </label>
                </div>
                <p className={`text-xs ${dk.textMuted}`} data-id="element-12699" data-mp="3a6ubq0k599s4">PNG, JPG up to 10MB</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Social Media Section */}
      <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12700" data-mp="35a3tb6kva23j">
        <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12701" data-mp="3096caxblfe3m">
          <MailIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12702" data-mp="2vbefpq0073t3" />
          Social Media
        </h3>
        <div className="space-y-4" data-id="element-12703" data-mp="yf75ao5i7iwq">
          {formData.socialLinks.sort((a, b) => {
          // Keep Personal Website at the top, sort others alphabetically
          if (a.platform === 'Personal Website') return -1;
          if (b.platform === 'Personal Website') return 1;
          return a.platform.localeCompare(b.platform);
        }).map((link, index) => <div key={index} className="flex items-center gap-2" data-id="element-12704" data-mp="2dyehurvdeuoc" data-mpi={index}>
                <div className="flex items-center w-32" data-id="element-12705" data-mp="2izvegb6tnuz4">
                  <img src={link.logo} alt={link.platform} className="h-5 w-5 mr-2 object-contain" onError={e => {
              e.currentTarget.src = '/placeholder-logo.png';
            }} data-id="element-12706" data-mp="36oert54qaxy2" />
                  <label className={`block text-sm font-medium ${dk.textSecondary}`} data-id="element-12707" data-mp="31p22mdqdxesb">
                    {link.platform}
                  </label>
                </div>
                <input type="url" className={inputClass} placeholder={`${link.platform} URL`} value={link.url} onChange={e => {
            const updatedLinks = [...formData.socialLinks];
            updatedLinks[index].url = e.target.value;
            setFormData({
              ...formData,
              socialLinks: updatedLinks
            });
          }} data-id="element-12708" data-mp="1qbpu3pjok7zt" />
                {link.platform !== 'Personal Website' && <button type="button" onClick={() => {
            const updatedLinks = formData.socialLinks.filter((_, i) => i !== index);
            setFormData({
              ...formData,
              socialLinks: updatedLinks
            });
          }} className="text-red-500 hover:text-red-700 p-1" data-id="element-12709" data-mp="v2ck7xta8pnz">
                    ×
                  </button>}
              </div>)}
          {/* Add New Platform */}
          <div className={`flex gap-2 items-center mt-4 pt-2 border-t ${dk.border}`} data-id="element-12710" data-mp="3fr7cryk17tls">
            <div className="relative w-full" data-id="element-12711" data-mp="3kofwi3ktw5p5">
              <input type="text" value={newPlatform} onChange={e => {
              setNewPlatform(e.target.value);
              setShowPlatformOptions(true);
            }} onFocus={() => setShowPlatformOptions(true)} onBlur={() => setTimeout(() => setShowPlatformOptions(false), 200)} className={inputClass} placeholder="Add another platform (Instagram, Twitter, etc.)" data-id="element-12712" data-mp="3ph0t1cv4cwwd" />
              {showPlatformOptions && <div className={`absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm ${dk.card}`} data-id="element-12713" data-mp="2e129ug688uhf">
                  {/* Show existing platforms that match search */}
                  {formData.socialLinks.filter(existing => existing.platform.toLowerCase().includes(newPlatform.toLowerCase()) && existing.platform !== 'Personal Website').map((existing, idx) => <div key={`existing-${idx}`} className={`px-4 py-2 cursor-not-allowed flex items-center ${dk.textMuted}`} data-id="element-12714" data-mp="2fihjjw1z1mc4" data-mpi={idx}>
                        <img src={existing.logo} alt={existing.platform} className="h-4 w-4 mr-2 object-contain" onError={e => {
                  e.currentTarget.src = '/placeholder-logo.png';
                }} data-id="element-12715" data-mp="1432leoy10off" />
                        {existing.platform} (already added)
                      </div>)}
                  {/* Show available platforms */}
                  {platformOptions.filter(p => p.name.toLowerCase().includes(newPlatform.toLowerCase()) && !formData.socialLinks.some(existing => existing.platform === p.name)).sort((a, b) => a.name.localeCompare(b.name)).map((platform, idx) => <div key={idx} onClick={() => {
                setFormData({
                  ...formData,
                  socialLinks: [...formData.socialLinks, {
                    platform: platform.name,
                    url: '',
                    logo: platform.logo
                  }]
                });
                setNewPlatform('');
                setShowPlatformOptions(false);
              }} className={`cursor-pointer px-4 py-2 flex items-center ${rt.dropdownItemHover} ${dk.textPrimary}`} data-id="element-12716" data-mp="3w083mfjc1yp1" data-mpi={idx}>
                        <img src={platform.logo} alt={platform.name} className="h-4 w-4 mr-2 object-contain" onError={e => {
                  e.currentTarget.src = '/placeholder-logo.png';
                }} data-id="element-12717" data-mp="d044weirt1qr" />
                        {platform.name}
                      </div>)}
                  {/* Show custom option if no matches */}
                  {newPlatform && !platformOptions.some(p => p.name.toLowerCase().includes(newPlatform.toLowerCase())) && !formData.socialLinks.some(existing => existing.platform.toLowerCase().includes(newPlatform.toLowerCase())) && <div onClick={() => {
                setFormData({
                  ...formData,
                  socialLinks: [...formData.socialLinks, {
                    platform: newPlatform,
                    url: '',
                    logo: `https://logo.clearbit.com/${newPlatform.toLowerCase().replace(/\s+/g, '')}.com`
                  }]
                });
                setNewPlatform('');
                setShowPlatformOptions(false);
              }} className={`cursor-pointer px-4 py-2 flex items-center ${rt.dropdownItemHover} ${rt.textPrimary}`} data-id="element-12718" data-mp="1fk0g5wi4soa3">
                        <span className="h-4 w-4 mr-2 flex items-center justify-center text-xs" data-id="element-12719" data-mp="1d3w2xtjhha9">
                          +
                        </span>
                        Add "{newPlatform}" as custom platform
                      </div>}
                </div>}
            </div>
          </div>
        </div>
      </div>
      {/* Store Settings */}
      {activeTab === 'store' && <>
          <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12720" data-mp="39rukx2iq4mac">
            <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12721" data-mp="39tylbahez35v">
              <StoreIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12722" data-mp="1tpb4g7sfxiv4" />
              Store Information
            </h3>
            <div className="space-y-4" data-id="element-12723" data-mp="1pmolkwdqz8a2">
              <div data-id="element-12724" data-mp="1plmy1sfjotbg">
                <label htmlFor="storeName" className={labelClass} data-id="element-12725" data-mp="1ppasctstdjr8">
                  Store Name
                </label>
                <input type="text" id="storeName" name="storeName" value={formData.storeName} onChange={handleInputChange} className={inputClass} data-id="element-12726" data-mp="1fp0fjaaoyecl" />
              </div>
              <div data-id="element-12727" data-mp="11xvi8euspy5f">
                <label htmlFor="storeDescription" className={labelClass} data-id="element-12728" data-mp="11yoiqy35vrf4">
                  Store Description
                </label>
                <textarea id="storeDescription" name="storeDescription" value={formData.storeDescription} onChange={handleInputChange} rows={4} className={inputClass} data-id="element-12729" data-mp="wy9a2jttr83h" />
              </div>
            </div>
          </div>
          <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12730" data-mp="3dli9uhuxk6z4">
            <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12731" data-mp="3dj4es9092urk">
              <CreditCardIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12732" data-mp="2kwjt9p6ghcvx" />
              Payment Methods
            </h3>
            <div className="space-y-4" data-id="element-12733" data-mp="2puv0rt9a3baz">
              {formData.paymentMethods.map((method, index) => <div key={index} className={`flex items-center justify-between ${dk.textPrimary}`} data-id="element-12734" data-mp="2twzuerzq87xm" data-mpi={index}>
                  <span data-id="element-12735" data-mp="2ypsxzcjgla84">{method}</span>
                  <button type="button" onClick={() => removeArrayItem('paymentMethods', index)} className="text-red-500 hover:text-red-700" data-id="element-12736" data-mp="226v9ms821ivv">
                    <TrashIcon className="h-4 w-4" data-id="element-12737" data-mp="39h9sbqcuurzf" />
                  </button>
                </div>)}
              <div className="flex gap-2" data-id="element-12738" data-mp="34j812i59zmcv">
                <input type="text" placeholder="Add payment method" className={`flex-1 ${inputClass}`} onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleArrayInput('paymentMethods', (e.target as HTMLInputElement).value);
                (e.target as HTMLInputElement).value = '';
              }
            }} data-id="element-12739" data-mp="3j84ekuq3prkv" />
                <button type="button" className={`px-3 py-2 rounded-md text-white ${rt.primaryButton}`} data-id="element-12740" data-mp="2vl636bgu99tt">
                  <PlusIcon className="h-5 w-5" data-id="element-12741" data-mp="arsvz2mff7nz" />
                </button>
              </div>
            </div>
          </div>
          <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12742" data-mp="ie6g6c2wl0co">
            <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12743" data-mp="nby7b3vq4ift">
              <TruckIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12744" data-mp="8fqrsgv9g56v" />
              Policies
            </h3>
            <div className="space-y-4" data-id="element-12745" data-mp="detv9wvle3cq">
              <div data-id="element-12746" data-mp="112beh40sjt43">
                <label htmlFor="storePolicy" className={labelClass} data-id="element-12747" data-mp="113w8msj71a1f">
                  Store Policy
                </label>
                <textarea id="storePolicy" name="storePolicy" value={formData.storePolicy} onChange={handleInputChange} rows={4} className={inputClass} data-id="element-12748" data-mp="160wk5iytb4zx" />
              </div>
              <div data-id="element-12749" data-mp="1k04jcbuz6d44">
                <label htmlFor="shippingPolicy" className={labelClass} data-id="element-12750" data-mp="1jzmggwdt50n8">
                  Shipping Policy
                </label>
                <textarea id="shippingPolicy" name="shippingPolicy" value={formData.shippingPolicy} onChange={handleInputChange} rows={4} className={inputClass} data-id="element-12751" data-mp="1447pawi40gq" />
              </div>
              <div data-id="element-12752" data-mp="3icr8k1l4z1ii">
                <label htmlFor="returnPolicy" className={labelClass} data-id="element-12753" data-mp="3id98b870bxv6">
                  Return Policy
                </label>
                <textarea id="returnPolicy" name="returnPolicy" value={formData.returnPolicy} onChange={handleInputChange} rows={4} className={inputClass} data-id="element-12754" data-mp="jwerhmfgr7oz" />
              </div>
            </div>
          </div>
        </>}
      {/* Author Settings */}
      {activeTab === 'author' && <>
          <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12755" data-mp="xud5urtsb44h">
            <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12756" data-mp="xsa63g2txwmk">
              <BookIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12757" data-mp="1a42so5642ofw" />
              Author Information
            </h3>
            <div className="space-y-4" data-id="element-12758" data-mp="1f35yvsxtv0pg">
              <div data-id="element-12759" data-mp="1f24cwtf9wtsy">
                <label htmlFor="authorPenName" className={labelClass} data-id="element-12760" data-mp="1f5s5nqcw9c6m">
                  Pen Name / Display Name
                </label>
                <input type="text" id="authorPenName" name="authorPenName" value={formData.authorPenName} onChange={handleInputChange} className={inputClass} placeholder="The name readers will see" data-id="element-12761" data-mp="1p2eok9lntt60" />
              </div>
              <div data-id="element-12762" data-mp="22uulcsnywuyh">
                <label htmlFor="authorBio" className={labelClass} data-id="element-12763" data-mp="27sctwhy2p85y">
                  Author Bio
                </label>
                <textarea id="authorBio" name="authorBio" value={formData.authorBio} onChange={handleInputChange} rows={6} className={inputClass} data-id="element-12764" data-mp="2cs926tdszlrb" />
              </div>
              <div data-id="element-12765" data-mp="1wt3mfhhzr4ss">
                <label className={labelClass} data-id="element-12766" data-mp="1wtn4qzbjzv7c">Writing Genres</label>
                <div className="space-y-2" data-id="element-12767" data-mp="1s1ln0xvm9c9x">
                  {formData.writingGenres.map((genre, index) => <div key={index} className={`flex items-center justify-between px-3 py-2 rounded-md ${dk.surfaceMuted} ${dk.textPrimary}`} data-id="element-12768" data-mp="25sqps8ua6koh" data-mpi={index}>
                      <span data-id="element-12769" data-mp="2fqoa4l90rxbt">{genre}</span>
                      <button type="button" onClick={() => removeArrayItem('writingGenres', index)} className="text-red-500 hover:text-red-700" data-id="element-12770" data-mp="2fpda8wip39pz">
                        <TrashIcon className="h-4 w-4" data-id="element-12771" data-mp="gfdhy9mymeim" />
                      </button>
                    </div>)}
                  <div className="flex gap-2" data-id="element-12772" data-mp="2s2gzfi3or38u">
                    <input type="text" placeholder="Add genre" className={`flex-1 ${inputClass}`} onKeyPress={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleArrayInput('writingGenres', (e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }} data-id="element-12773" data-mp="2x0s8vrq2jbqj" />
                    <button type="button" className={`px-3 py-2 rounded-md text-white ${rt.primaryButton}`} data-id="element-12774" data-mp="24dfnhkvn99yt">
                      <PlusIcon className="h-5 w-5" data-id="element-12775" data-mp="2krj3879kghg" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12776" data-mp="2lldjy8xa4od2">
            <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12777" data-mp="2hir0avari9qy">
              <BookIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12778" data-mp="2cow6v05cugeo" />
              Published Works
            </h3>
            <div className="space-y-4" data-id="element-12779" data-mp="22st720xz6tuw">
              {formData.publishedBooks.map((book, index) => <div key={index} className={`flex items-center justify-between px-3 py-2 rounded-md ${dk.surfaceMuted} ${dk.textPrimary}`} data-id="element-12780" data-mp="1xu84vvz5y03t" data-mpi={index}>
                  <span data-id="element-12781" data-mp="1otjqh05uxxqe">{book}</span>
                  <button type="button" onClick={() => removeArrayItem('publishedBooks', index)} className="text-red-500 hover:text-red-700" data-id="element-12782" data-mp="1oubkw7npxpf3">
                    <TrashIcon className="h-4 w-4" data-id="element-12783" data-mp="3j82sz2qeg18f" />
                  </button>
                </div>)}
              <div className="flex gap-2" data-id="element-12784" data-mp="3qopu0u46xge7">
                <input type="text" placeholder="Add published book" className={`flex-1 ${inputClass}`} onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleArrayInput('publishedBooks', (e.target as HTMLInputElement).value);
                (e.target as HTMLInputElement).value = '';
              }
            }} data-id="element-12785" data-mp="3vp56ljgnejv4" />
                <button type="button" className={`px-3 py-2 rounded-md text-white ${rt.primaryButton}`} data-id="element-12786" data-mp="3lrhd0wus3iej">
                  <PlusIcon className="h-5 w-5" data-id="element-12787" data-mp="wzn79sv0he2w" />
                </button>
              </div>
            </div>
          </div>
          <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12788" data-mp="113cv2g3v3rmv">
            <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12789" data-mp="1s437poc9gsuf">
              <BookIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12790" data-mp="1x48tinvunl4l" />
              Current Projects
            </h3>
            <div className="space-y-4" data-id="element-12791" data-mp="1ncbet75mub14">
              {formData.currentProjects.map((project, index) => <div key={index} className={`flex items-center justify-between px-3 py-2 rounded-md ${dk.surfaceMuted} ${dk.textPrimary}`} data-id="element-12792" data-mp="19cafvx8xhdoa" data-mpi={index}>
                  <span data-id="element-12793" data-mp="zevz1wj3emvg">{project}</span>
                  <button type="button" onClick={() => removeArrayItem('currentProjects', index)} className="text-red-500 hover:text-red-700" data-id="element-12794" data-mp="zfxjuspwunqd">
                    <TrashIcon className="h-4 w-4" data-id="element-12795" data-mp="2tqkgc5csr1j0" />
                  </button>
                </div>)}
              <div className="flex gap-2" data-id="element-12796" data-mp="2ypdtfwo68qin">
                <input type="text" placeholder="Add current project" className={`flex-1 ${inputClass}`} onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleArrayInput('currentProjects', (e.target as HTMLInputElement).value);
                (e.target as HTMLInputElement).value = '';
              }
            }} data-id="element-12797" data-mp="n5g1u5nwf7p7" />
                <button type="button" className={`px-3 py-2 rounded-md text-white ${rt.primaryButton}`} data-id="element-12798" data-mp="1ftlpb06c7asq">
                  <PlusIcon className="h-5 w-5" data-id="element-12799" data-mp="1arwb5bntih0f" />
                </button>
              </div>
            </div>
          </div>
        </>}
      {/* Narrator Settings */}
      {activeTab === 'narrator' && <>
          <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12800" data-mp="1gih3uqh1u4vt">
            <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12801" data-mp="1gkaeegus9993">
              <HeadphonesIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12802" data-mp="1pivrvxledy30" />
              Voice Profile
            </h3>
            <div className="space-y-4" data-id="element-12803" data-mp="1uii7gdop7qez">
              <div data-id="element-12804" data-mp="1uja13iyqje2m">
                <label htmlFor="narratorName" className={labelClass} data-id="element-12805" data-mp="1ufm6shlgunmu">
                  Narrator Display Name
                </label>
                <input type="text" id="narratorName" name="narratorName" value={formData.narratorName} onChange={handleInputChange} className={inputClass} placeholder="The name listeners will see" data-id="element-12806" data-mp="24fmqi4n8z5rk" />
              </div>
              <div data-id="element-12807" data-mp="2i5gkt2hiui8o">
                <label className={labelClass} data-id="element-12808" data-mp="2n6eas18rwgkq">Voice Types</label>
                <div className="space-y-2" data-id="element-12809" data-mp="2n6xgmjl5feie">
                  {formData.voiceTypes.map((type, index) => <div key={index} className={`flex items-center justify-between px-3 py-2 rounded-md ${dk.surfaceMuted} ${dk.textPrimary}`} data-id="element-12810" data-mp="2x8hyjoms66xr" data-mpi={index}>
                      <span data-id="element-12811" data-mp="2ne83u0ad8wjv">{type}</span>
                      <button type="button" onClick={() => removeArrayItem('voiceTypes', index)} className="text-red-500 hover:text-red-700" data-id="element-12812" data-mp="2jbl5rhn3qfe8">
                        <TrashIcon className="h-4 w-4" data-id="element-12813" data-mp="3b2raychoj3ad" />
                      </button>
                    </div>)}
                  <div className="flex gap-2" data-id="element-12814" data-mp="329xj7fl1qbfm">
                    <input type="text" placeholder="Add voice type" className={`flex-1 ${inputClass}`} onKeyPress={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleArrayInput('voiceTypes', (e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }} data-id="element-12815" data-mp="3295zb20ta256" />
                    <button type="button" className={`px-3 py-2 rounded-md text-white ${rt.primaryButton}`} data-id="element-12816" data-mp="39ltuudki0vdk">
                      <PlusIcon className="h-5 w-5" data-id="element-12817" data-mp="62iq3ijx0iic" />
                    </button>
                  </div>
                </div>
              </div>
              <div data-id="element-12818" data-mp="3sid6pamosxdt">
                <label className={labelClass} data-id="element-12819" data-mp="3shtodst4k6z9">Accents</label>
                <div className="space-y-2" data-id="element-12820" data-mp="3sg8u84aq2q1x">
                  {formData.accentsProficient.map((accent, index) => <div key={index} className={`flex items-center justify-between px-3 py-2 rounded-md ${dk.surfaceMuted} ${dk.textPrimary}`} data-id="element-12821" data-mp="ot9vk90s7org" data-mpi={index}>
                      <span data-id="element-12822" data-mp="24ldsu9j5g8el">{accent}</span>
                      <button type="button" onClick={() => removeArrayItem('accentsProficient', index)} className="text-red-500 hover:text-red-700" data-id="element-12823" data-mp="24kayfrmmbntx">
                        <TrashIcon className="h-4 w-4" data-id="element-12824" data-mp="1qll1qd3pnq63" />
                      </button>
                    </div>)}
                  <div className="flex gap-2" data-id="element-12825" data-mp="1locgg3n9n60m">
                    <input type="text" placeholder="Add accent" className={`flex-1 ${inputClass}`} onKeyPress={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleArrayInput('accentsProficient', (e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }} data-id="element-12826" data-mp="20kaejrgr2kfh" />
                    <button type="button" className={`px-3 py-2 rounded-md text-white ${rt.primaryButton}`} data-id="element-12827" data-mp="3nym6zk4z2daa">
                      <PlusIcon className="h-5 w-5" data-id="element-12828" data-mp="2vdchgdydsyv7" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12829" data-mp="359xo13q1ui15">
            <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12830" data-mp="3a0xogiydni8e">
              <HeadphonesIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12831" data-mp="2cmbn6387hrhz" />
              Equipment & Space
            </h3>
            <div className="space-y-4" data-id="element-12832" data-mp="2hjbra8lhwk6g">
              <div data-id="element-12833" data-mp="2hia8fhy8mte6">
                <label htmlFor="audioEquipment" className={labelClass} data-id="element-12834" data-mp="yq0jq40k3orb">
                  Audio Equipment
                </label>
                <textarea id="audioEquipment" name="audioEquipment" value={formData.audioEquipment} onChange={handleInputChange} rows={4} className={inputClass} placeholder="List your microphone, interface, and other equipment..." data-id="element-12835" data-mp="up89b7fy7dtg" />
              </div>
              <div data-id="element-12836" data-mp="ayv2bkeywoew">
                <label htmlFor="recordingSpace" className={labelClass} data-id="element-12837" data-mp="aycwbw2i6vts">
                  Recording Space
                </label>
                <textarea id="recordingSpace" name="recordingSpace" value={formData.recordingSpace} onChange={handleInputChange} rows={4} className={inputClass} placeholder="Describe your recording space and acoustic treatment..." data-id="element-12838" data-mp="6vyk3utcaubh" />
              </div>
            </div>
          </div>
          <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12839" data-mp="1kxcq798ey8mp">
            <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12840" data-mp="16x1zc673su1j">
              <HeadphonesIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12841" data-mp="1bvmzk5mcvdpz" />
              Sample Clips
            </h3>
            <div className="space-y-4" data-id="element-12842" data-mp="x67bixe7lmdz">
              <div className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${dk.border} ${dk.surface}`} data-id="element-12843" data-mp="x55pjxvnnfhh">
                <div className="space-y-1 text-center" data-id="element-12844" data-mp="o7n1slqmmn1w">
                  <div className={`flex text-sm ${dk.textSecondary}`} data-id="element-12845" data-mp="t5ojb28emeb9">
                    <label className={`relative cursor-pointer rounded-md font-medium focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 ${rt.textPrimary} ${rt.inputFocus} ${dk.surface}`} data-id="element-12846" data-mp="t5wy4zo37tp3">
                      <span data-id="element-12847" data-mp="3vqbgvr9cdufj">Upload audio files</span>
                      <input type="file" className="sr-only" multiple accept="audio/*" onChange={e => {
                    const files = Array.from(e.target.files || []);
                    setFormData(prev => ({
                      ...prev,
                      sampleClips: [...prev.sampleClips, ...files]
                    }));
                  }} data-id="element-12848" data-mp="3vt623l06jja0" />
                    </label>
                  </div>
                  <p className={`text-xs ${dk.textMuted}`} data-id="element-12849" data-mp="sch26hv3r7tb">
                    MP3, WAV up to 50MB
                  </p>
                </div>
              </div>
              {formData.sampleClips.map((clip, index) => <div key={index} className={`flex items-center justify-between px-3 py-2 rounded-md ${dk.surfaceMuted} ${dk.textPrimary}`} data-id="element-12850" data-mp="x9fxv4uezmtm" data-mpi={index}>
                  <span data-id="element-12851" data-mp="zzx4o4toqsh7">{clip.name}</span>
                  <button type="button" onClick={() => {
              setFormData(prev => ({
                ...prev,
                sampleClips: prev.sampleClips.filter((_, i) => i !== index)
              }));
            }} className="text-red-500 hover:text-red-700" data-id="element-12852" data-mp="100yek5yyt8xc">
                    <TrashIcon className="h-4 w-4" data-id="element-12853" data-mp="1jq8tviustrux" />
                  </button>
                </div>)}
            </div>
          </div>
        </>}
      {/* Content Preferences */}
      <div className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-12854" data-mp="1smrnyyernlt3">
        <h3 className={`text-lg font-medium mb-4 flex items-center ${dk.textHeading}`} data-id="element-12855" data-mp="3a68m15qwx9a">
          <TagIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-12856" data-mp="3uhs6te9p47ir" />
          {getContentLabel().title}
        </h3>
        <div className="space-y-4" data-id="element-12857" data-mp="29zzsh6oxi40l">
          <div data-id="element-12858" data-mp="2exj747pjgit2">
            <div className="flex gap-4 mb-4" data-id="element-12859" data-mp="2evyeinmsb9xu">
              <button type="button" className={`px-4 py-2 rounded-md text-sm font-medium ${activeList === 'include' ? 'bg-green-100 text-green-800 border border-green-300' : `${dk.surfaceMuted} ${dk.textSecondary}`}`} onClick={() => setActiveList('include')} data-id="element-12860" data-mp="2eyucud2jo0mt">
                {getContentLabel().includeLabel}
              </button>
              <button type="button" className={`px-4 py-2 rounded-md text-sm font-medium ${activeList === 'exclude' ? 'bg-red-100 text-red-800 border border-red-300' : `${dk.surfaceMuted} ${dk.textSecondary}`}`} onClick={() => setActiveList('exclude')} data-id="element-12861" data-mp="1w4fe8ok5a9zi">
                {getContentLabel().excludeLabel}
              </button>
            </div>
            <input type="text" placeholder="Search content topics..." value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (inputValue.trim()) {
                setFormData(prev => ({
                  ...prev,
                  contentPreferences: {
                    ...prev.contentPreferences,
                    [activeList]: [...prev.contentPreferences[activeList], inputValue.trim()]
                  }
                }));
                setInputValue('');
              }
            }
          }} className={`w-full border px-3 py-2 text-sm rounded-md mb-2 focus:outline-none ${dk.inputOnCard} ${activeList === 'include' ? 'border-green-300 focus:ring-green-500' : 'border-red-300 focus:ring-red-500'}`} data-id="element-12862" data-mp="3gjzepreffg3i" />
            {/* Selected Topics */}
            <div className="mt-4 space-y-4" data-id="element-12863" data-mp="23bvz43ndu3c3">
              {/* Included Topics */}
              {formData.contentPreferences.include.length > 0 && <div data-id="element-12864" data-mp="27bxu5tym3yie">
                  <h4 className={`text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-12865" data-mp="27flp8xjpgsz8">
                    {getContentLabel().includeLabel}:
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-12866" data-mp="2h9kmbi4x9mkb">
                    {formData.contentPreferences.include.map(topic => <span key={topic} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 border border-green-300" data-id="element-12867" data-mp="2r0yh5clxhy74">
                        {topic}
                        <button type="button" onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      contentPreferences: {
                        ...prev.contentPreferences,
                        include: prev.contentPreferences.include.filter(t => t !== topic)
                      }
                    }));
                  }} className="ml-2 text-green-600 hover:text-green-800" data-id="element-12868" data-mp="30023cpge6tpu">
                          ×
                        </button>
                      </span>)}
                  </div>
                </div>}
              {/* Excluded Topics */}
              {formData.contentPreferences.exclude.length > 0 && <div data-id="element-12869" data-mp="3troj08lqcq4s">
                  <h4 className={`text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-12870" data-mp="3tr6gwvcdzhoy">
                    {getContentLabel().excludeLabel}:
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-12871" data-mp="6qen9gn4l2a">
                    {formData.contentPreferences.exclude.map(topic => <span key={topic} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800 border border-red-300" data-id="element-12872" data-mp="3nd0un241qcdl">
                        {topic}
                        <button type="button" onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      contentPreferences: {
                        ...prev.contentPreferences,
                        exclude: prev.contentPreferences.exclude.filter(t => t !== topic)
                      }
                    }));
                  }} className="ml-2 text-red-600 hover:text-red-800" data-id="element-12873" data-mp="izb3mft7y60t">
                          ×
                        </button>
                      </span>)}
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
      {/* Save Button */}
      <div className="flex justify-end" data-id="element-12874" data-mp="xl84sz2ho7d8">
        <button type="submit" className={`inline-flex items-center px-4 py-2 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${rt.primaryButton} ${rt.inputFocus}`} data-id="element-12875" data-mp="xm1f2a0npf04">
          <SaveIcon className="h-5 w-5 mr-2" data-id="element-12876" data-mp="ot67h9npv776" />
          {saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>
    </form>;
};
export default VendorProfileSettings;